//
// AM-4825 — the one place that decides what a traveler is told to DO at drop-off and pick-up.
//
// A storage location is either staffed (hand bags to a person, show them the confirmation) or
// self-service (a storage room the guest opens with an access code or key card, nobody to show
// anything to). The booking timeline, the receipt views and the drop-off / pick-up modals each used
// to pick their own copy key, and only the confirmation modal knew about self-service — so a guest
// at a Salto hotel was told to "show the pickup confirmation to the staff" (reported from Comwell
// Portside, 2026-09-09). Every surface now asks here, with the same inputs, so the two shop types
// cannot drift apart again. Keep it free of Angular and of domain interfaces: primitives in, a
// translation key out, so it is trivially unit-tested and reusable by the booking page that will
// replace the timeline.
//

export interface BookingGuestCopyContext {
  /**
   * The guest deposits and collects bags in a storage room on their own (access code / key card).
   * Derived on the client from the shop's `securitySolution`; `undefined` (shop not loaded yet) is
   * treated as staffed, which is the safe default — staff copy at a self-service shop is the bug
   * this fixes, but self-service copy at a staffed shop would send a traveler hunting for a room
   * that does not exist.
   */
  isSelfService?: boolean;
  /** The traveler settled directly with the staff instead of by card. Never true at a self-service shop in practice. */
  paidDirectly?: boolean;
}

/**
 * Title of the timeline's drop-off step. The self-service title is the one the guest drop-off
 * wizard already uses for a storage room (`confirmation-step`), so the two surfaces agree.
 */
export function dropOffStepTitleKey(ctx: BookingGuestCopyContext): string {
  return ctx.isSelfService ? 'DROP_OFF_YOUR_LUGGAGE' : 'HAND_OVER_YOUR_LUGGAGE';
}

/** Hint under the drop-off step while the booking is still waiting for the guest to arrive. */
export function dropOffStepHintKey(ctx: BookingGuestCopyContext): string {
  return ctx.isSelfService ? 'BOOKING_TIMELINE_SELF_SERVICE_DROP_OFF_HINT' : 'BOOKING_TIMELINE_START_TIMER_SHORT';
}

/** Hint under the pick-up step while the bags are stored (storage timer running). */
export function pickUpStepHintKey(ctx: BookingGuestCopyContext): string {
  return ctx.isSelfService ? 'BOOKING_TIMELINE_SELF_SERVICE_PICK_UP_HINT' : 'STOP_STORAGE_TIMER_AND_SHOW_RECEIPT';
}

/**
 * What to do once the storage has been paid for: the pick-up step's completed state and every
 * "view receipt" action (timeline, options menu, the confirmation shown after tapping Pick up).
 * Direct payment keeps its own instruction on both shop types — the receipt is what the traveler
 * pays against, and a self-service shop that somehow ends up there has no better copy to offer.
 */
export function afterPaymentInstructionKey(ctx: BookingGuestCopyContext): string {
  if (ctx.paidDirectly) {
    return 'SHOW_THE_RECEIPT_AND_PAY_IN_SHOP';
  }
  return ctx.isSelfService ? 'STORAGE_ENDED_COLLECT_BAGS_FROM_STORAGE_ROOM' : 'SHOW_THE_RECEIPT_TO_THE_STAFF';
}

/**
 * The "you're all set" line in the drop-off modal and the drop-off wizard. Translate it with the shop
 * name as `{ location }`: the sentence carries a `{{location}}` placeholder (AM-5036), because a shop
 * name glued on after the text cannot follow German word order or Polish and Russian cases. The old
 * fragment keys (START_TIMER_AND_*_AT) stay seeded for installed native builds.
 */
export function dropOffReadyInstructionKey(ctx: BookingGuestCopyContext): string {
  return ctx.isSelfService ? 'DROPOFF_READY_DEPOSIT_BAGS_AT_LOCATION' : 'DROPOFF_READY_HAND_OVER_LUGGAGE_AT_LOCATION';
}

/** The line under the "Picked up!" banner in the pick-up modal. */
export function pickedUpInstructionKey(ctx: BookingGuestCopyContext): string {
  return ctx.isSelfService ? 'STORAGE_ENDED_COLLECT_BAGS_FROM_STORAGE_ROOM' : 'SHOW_TO_PARTNER_STAFF';
}

/**
 * The "are you a guest here?" question asked at a shop that serves both its own guests and paying
 * non-guests. Takes the shop's stored `locationType` key ('Hotel', 'Hostel', …); anything else, or
 * no type at all, gets the generic wording.
 *
 * Compared as KEYS (`StorageLocationTypeKey`), which is what the field stores. The enum's own
 * members are the DISPLAY strings, so `StorageLocationType.Hotel` only ever matched because 'Hotel'
 * happens to be both — `StorageLocationType.ConvenienceStore` would compare against
 * 'Convenience Store' and silently never match (AM-4718).
 */
export function guestStatusQuestionKey(locationType: string | null | undefined): string {
  switch (locationType) {
    case 'Hotel':
      return 'ARE_YOU_A_GUEST_AT_THE_HOTEL';

    case 'Hostel':
      return 'ARE_YOU_A_GUEST_AT_THE_HOSTEL';

    default:
      return 'ARE_YOU_A_GUEST_HERE';
  }
}
